import { useLocation } from "@reach/router";
import { navigate, Slice } from "gatsby";
import moment from "moment-mini";
import React, { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { TP_REDEMPTION_RATE } from "../../../@types/Constants";
import IRoom from "../../../@types/IRoom";
import { useAppDispatch } from "../../../hooks";
import { IUseRoomDetail, useRoomDetail } from "../../../hooks/useRoomDetail";
import { useWebFramed } from "../../../hooks/useWebFramed";
import { setEditReservationCheckout } from "../../../redux/slices/Checkout/editReservationCheckout";
import { unlockPricing } from "../../../redux/slices/Rate/rate";
import { guestServiceProvider } from "../../../services/ServiceProvider";
import { customFormatDate } from "../../../services/dates";
import {
  buildFetchRatesUrl,
  fetchRoomsAndRatesASE,
} from "../../../services/rates";
import { convertArrayToObject } from "../../../utils/helpers";
import { IEditReservationButtonProps } from "./EditReservationButtonProps";
import { LoginModalSliceProvider } from "../../../context/SliceContexts/LoginModalContext";

const EditReservationButton: React.FC<IEditReservationButtonProps> = (
  props
) => {
  const dispatch = useAppDispatch();
  const isWebFramed = useWebFramed();
  const [showEditButton, setShowEditButton] = useState(false);
  const [hideEditButtonLoader, setHideEditButtonLoader] = useState(false);
  const [isRateAdvance, setIsRateAdvance] = useState(null);
  const [rooms, setRooms] = useState(null);
  const rateCode = props.reservation.ratePlanCode;
  const roomCode = props.reservation.roomTypeCode;
  const [isMember, setIsMember] = useState(props.memberId ? true : false);
  const isLoggedIn = useSelector((state: any) => state.member.isLoggedIn);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [roomCount, setRoomCount] = useState(6);
  const location = useLocation();
  const _roomDetail: IUseRoomDetail = {
    hotelCode: props.reservation?.hotel?.id,
    roomCode: roomCode,
    resId: props.confirmationID,
  };
  const [_loadingRoomDetail, roomDetail, allRooms] = useRoomDetail(_roomDetail);
  const isFrom = "edit-reservation";
  const startDate = props.reservation
    ? customFormatDate(props.reservation.start)
    : "";
  const endDate = props.reservation
    ? customFormatDate(props.reservation.end)
    : "";
  const is24HoursBefore = moment()
    .add(1, "days")
    .isBefore(startDate + " 13:00");

  let groupCode: string | null = null;
  if (rateCode.indexOf("G:") == 0) {
    // if rate code is group code
    groupCode = rateCode.replace("G:", "");
  }
  function handleLoginModal(show: boolean) {
    setShowLoginModal(show);
  }
  const handleEditReservation = () => {
    const propsReservation = props.reservation;
    const childrenAges = [];
    if (propsReservation?.count?.child > 0) {
      let childCount = propsReservation?.count?.child;
      while (childCount > 0) {
        childrenAges.push("");
        childCount--;
      }
    }
    const _room: IRoom = {
      id: uuidv4(),
      adults: propsReservation.count.adult,
      children: propsReservation.count.child || 0,
      childrenAges: childrenAges,
    };
    const newRoom: Array<IRoom> = [];
    newRoom.push(_room);

    let room: any | null = rooms ? rooms[roomCode] : null;
    if (!room && roomDetail) {
      room = {
        Name: roomDetail.name,
        Description: roomDetail.description,
        Occupancy: roomDetail.occupancy,
        Image:
          (roomDetail.imageUrls &&
            roomDetail.imageUrls.length &&
            roomDetail.imageUrls[0]) ||
          null,
        RoomCode: propsReservation.roomTypeCode,
        FromRate:
          propsReservation.roomRates && propsReservation.roomRates[0].roomRate,
        FromRateCode: propsReservation.ratePlanCode,
        FromRateType: "",
        BaseRate: 0,
        BaseRateCode: "",
        BaseRateType: "",
        Rates: [],
      };
    }
    const rate =
      room.Rates && room.Rates.filter((r: any) => r.rateCode == rateCode)[0];
    newRoom[0] = { ...newRoom[0], room: room, rate: rate, services: null };
    const newRoomObj: { [x: string]: IRoom } = convertArrayToObject(
      newRoom,
      "id"
    );
    const resGuest = propsReservation.guests && propsReservation.guests[0];

    const editCheckout = {
      Start: startDate,
      End: endDate,
      Crs: propsReservation.Crs || "synxis",
      HotelCode: props.reservation.hotel.id,
      EditReservationRate: propsReservation.ratePlanCode,
      discount: null,
      promotionCode: propsReservation?.promotionCode,
      groupCode: null,
      Rooms: newRoomObj,
      Services: null,
      GuestDetails: {
        firstName: resGuest.givenName,
        lastName: resGuest.surname,
        email: resGuest.email,
        id: resGuest.MembershipID || props.memberId,
        phno: resGuest.PhoneNumber ? resGuest.phoneNumber : "NA",
      },
      Step: "select-room",
      isModalOpen: false,
      isMember: isMember,
      originalReservation: {
        reservation: props.reservation,
        confirmationID: props.confirmationID,
        originalRoom: room,
        originalRate: rate,
        hotel: props.hotel,
        services: propsReservation?.services || [],
      },
    };
    dispatch(setEditReservationCheckout(editCheckout));

    if (
      propsReservation.ratePlanCode &&
      TP_REDEMPTION_RATE.includes(propsReservation.ratePlanCode) &&
      !isLoggedIn
    ) {
      handleLoginModal(true);
      return;
    }

    navigate("/edit-reservation/select-room", {
      state: {
        reservation: props.reservation,
        confirmationID: props.confirmationID,
        room: room,
        rate: rate,
        hotel: props.hotel,
        isMember: isMember,
        displayRoomCount: roomCount,
      },
    });
  };

  // Fetch pricing.
  useEffect(() => {
    let didCancel = false;
    const fetchRooms = async (_apiUrl: string) => {
      let fetchedRooms = null;
      const guestService = await guestServiceProvider();
      const response = await guestService.getHotelAvailability(
        props.reservation.count.adult,
        props.reservation.count.child,
        "",
        props.reservation.hotel.id,
        props.reservation.chainCode,
        startDate,
        endDate,
        "NonRoom",
        "",
        rateCode && TP_REDEMPTION_RATE.includes(rateCode) ? "" : rateCode,
        "Retail",
        null
      );
      // const data = await coreGet(apiUrl, source);
      if (response !== null) {
        // fetchedRooms = (Object.values(response.hotelAvailablity.queryHotel.rooms) as Array<any>)[0].Rooms;
        fetchedRooms = response.hotelAvailablity?.queryHotel?.rooms;
        if (fetchedRooms !== null) {
          let roomCount;
          roomCount = fetchedRooms.filter(
            (room) => room.accessible === false
          ).length;

          // If no non-accessible rooms, count accessible rooms
          roomCount =
            roomCount ||
            fetchedRooms.filter((room) => room.accessible === true).length;

          setRoomCount(roomCount);
        }
        if (!didCancel) {
          // setRooms(fetchedRooms);
          const thisRoomRates = fetchRoomsAndRatesASE(
            fetchedRooms,
            null,
            null,
            groupCode ? groupCode : null
          );
          setRooms(convertArrayToObject(thisRoomRates, "code"));
          // setRoomRates(thisRoomRates);
        }
      }
    };
    // ASE - Availibilty Call
    (async () => {
      const apiUrl = buildFetchRatesUrl(
        props.reservation.Crs,
        props.reservation.hotel.id, //'12667'
        props.reservation.Start,
        props.reservation.End,
        props.reservation.count.adult,
        props.reservation.count.child || 0,
        null,
        groupCode,
        rateCode
      );
      if (apiUrl && !rooms) {
        await fetchRooms(apiUrl);
      }
    })();

    return () => {
      didCancel = true;
    };
  }, [rateCode]);

  useEffect(() => {
    if (props.memberId) {
      setIsMember(true);
    }
    dispatch(unlockPricing(true));
  }, [props.memberId]);

  useEffect(() => {
    if (rooms) {
      const resRoomRates = rooms[roomCode] && (rooms[roomCode] as any).Rates;
      let reservationRoomRate;
      if (resRoomRates) {
        if (resRoomRates[rateCode]) {
          reservationRoomRate = resRoomRates[rateCode];
        } else {
          reservationRoomRate = (resRoomRates as Array<any>).filter(
            (r: any) => r.RateCode == rateCode
          )[0];
        }
      }
      // let reservationRoomRate = rooms[roomCode] && rooms[roomCode].Rates[rateCode];
      const isRateAdvance = reservationRoomRate && reservationRoomRate.advanced;
      setIsRateAdvance(isRateAdvance);
    }
  }, [rooms]);

  useEffect(() => {
    let isHelloBucksUsed = false;
    if (props.reservation.ResBucksRedeemed) {
      const bucksSplitArr = props.reservation.ResBucksRedeemed.split("=");
      const bucksRedeemed = bucksSplitArr[1]
        ? bucksSplitArr[1].replace(";", "").trim()
        : 0;
      isHelloBucksUsed = parseInt(bucksRedeemed) > 0;
    }
    const showEditButton =
      isRateAdvance !== null &&
      !isRateAdvance &&
      is24HoursBefore &&
      !isHelloBucksUsed &&
      roomDetail;
    if (isWebFramed && roomDetail && allRooms) {
      props.hideSpinner && props.hideSpinner(false);
      handleEditReservation();
    } else {
      setShowEditButton(showEditButton);
      if (!showEditButton) {
        setHideEditButtonLoader(true);
      }
    }
  }, [isRateAdvance, roomDetail, is24HoursBefore]);
  return (
    <div>
      {showEditButton
        ? rooms &&
          !isWebFramed && (
            <Button
              id="edit-reservation-button"
              variant="link"
              size="sm"
              className="p-0"
              onClick={handleEditReservation}
            >
              Edit Reservation
            </Button>
          )
        : !isWebFramed &&
          !hideEditButtonLoader && (
            <Spinner animation={"border"} size="sm" className="m-2" />
          )}

      <LoginModalSliceProvider
        value={{
          show: showLoginModal,
          onHide: () => handleLoginModal(false),
          location: location,
          welcomeSubHeading: "Sign In To Travel Pass to Edit this Reservation",
          isEditReservation: true,
          isFrom: isFrom,
        }}
      >
        <Slice alias="login-modal" />
      </LoginModalSliceProvider>
    </div>
  );
};

export default EditReservationButton;
